import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mic, Brain, Briefcase, Rocket, Sparkles, GraduationCap,
  TrendingUp, Mic2, Cpu, Radio, Wrench, Lightbulb, Users,
  ArrowRight, Check, Calendar, Sun, Building2, Heart,
  ShieldCheck, Trophy, Award, Menu, X, MapPin, Mail, Phone, Instagram,
  PlayCircle, ChevronRight
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ Asset URLs ============
const IMG = {
  logo: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/ff5t7wuy_JPEG%20image-40D3-90E9-38-0.jpeg",
  logoMain: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/z0ndkny3_IMG_0256.PNG",
  logoB: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/f4jkk7bc_C8015BF8-178C-4457-A146-28ED110A68B6.PNG",
  logoBlastWorkshop: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/nkjuum7i_JPEG%20image-47AC-B9D5-CA-0.jpeg",
  partnerRYC: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/h544xl2d_4067BE6D-495A-46C9-BFC6-4E1B81BE4E50.PNG",
  founder: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/ln0u3if3_Screenshot%202026-05-19%20at%202.52.15%E2%80%AFPM.png",
  founder2: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/rcbwdqwz_IMG_0325.jpeg",
  philipTeaching: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/9l44k64b_IMG_2181.PNG",
  trackMedia: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/tl9ar4i6_IMG_2181.PNG",
  trackAi: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/sss2e2zd_Screenshot%202026-05-19%20at%203.01.02%E2%80%AFPM.png",
  trackHustlers: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/4xy4ukcs_Screenshot%202026-05-19%20at%203.05.39%E2%80%AFPM.png",
  hero: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/llw9xztp_IMG_0894.JPG",
  partnerNeon: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/9w41y3ne_IMG_3001.jpg",
  partnerF3: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/4gixf7j6_F3_LOGO.png",
  logoAi: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/tyv38ur1_1B2510DF-C0BF-41C1-9F32-AD6A95D530B4.PNG",
  logoHustlers: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/057qpike_IMG_0921.PNG",
};
// Ai advantage logo URL collided with founder (both used same URL in design json).
// We'll use the Ai logo carefully; the actual founder portrait will use philipTeaching.

// ============ Reveal Hook ============
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ============ Nav ============
const NAV_LINKS = [
  { label: "Programs", href: "/#programs" },
  { label: "Outcomes", href: "/#outcomes" },
  { label: "Partners", href: "/#partners" },
  { label: "Contact", href: "/#contact" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-300 bg-[#FAF6EC] border-b ${
        scrolled ? "border-zinc-900/10 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.15)]" : "border-zinc-900/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-3 md:py-0 md:h-24 flex items-center justify-between gap-3 md:gap-6">
        <a href="/" data-testid="nav-logo" className="flex items-center gap-2.5 md:gap-4 shrink-0 flex-1 lg:flex-initial min-w-0">
          <span
            className="relative inline-flex w-20 h-20 md:w-24 md:h-24 lg:w-20 lg:h-20 xl:w-24 xl:h-24 shrink-0"
            style={{
              WebkitMaskImage: `url(${IMG.logoB})`,
              maskImage: `url(${IMG.logoB})`,
              WebkitMaskSize: "contain",
              maskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              maskPosition: "center",
              backgroundColor: "#FF2A2A",
            }}
            aria-label="B.L.A.S.T. Academy"
            role="img"
          />
          <div className="flex items-center gap-3 md:gap-4 lg:gap-3 xl:gap-5 flex-1 min-w-0">
            <div className="leading-none flex-1 lg:flex-initial min-w-0">
              <div className="text-[9px] sm:text-[10px] md:text-[10px] uppercase tracking-[0.42em] md:tracking-[0.32em] text-zinc-700 font-semibold mb-1 md:mb-0.5 pl-0.5 md:pl-0">
                The
              </div>
              <div className="font-display font-bold text-[2.4rem] sm:text-[2.85rem] md:text-3xl lg:text-2xl xl:text-3xl tracking-tight leading-none">
                B.L.A.S.T.
              </div>
              <div className="text-[10px] sm:text-[12px] md:text-xs tracking-[1.6em] sm:tracking-[1.9em] md:tracking-[0.6em] text-[#FF2A2A] font-semibold mt-2 md:mt-1 pl-0.5 md:pl-0">
                ACADEMY
              </div>
              <div className="md:hidden text-[8px] uppercase tracking-[0.18em] text-zinc-600 font-medium leading-snug mt-2 pl-0.5">
                Business, Leadership,<br />
                Ai, Streaming &amp; Technology
              </div>
            </div>
            <span className="hidden md:block lg:hidden xl:block w-px h-9 bg-zinc-900/15" />
            <div className="hidden md:block lg:hidden xl:block text-[10px] lg:text-[11px] uppercase tracking-[0.16em] text-zinc-600 font-medium leading-snug whitespace-nowrap">
              Business, Leadership,<br />
              Ai, Streaming &amp; Technology
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-[12px] uppercase tracking-[0.18em] text-zinc-700 hover:text-zinc-900 transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1.5 h-px w-0 bg-[#FF2A2A] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <a
            href="/#contact"
            data-testid="nav-partner-cta"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF2A2A] hover:bg-[#E60000] transition-colors text-white text-[12px] uppercase tracking-[0.18em] font-medium shadow-[0_0_24px_rgba(255,42,42,0.35)] hover:shadow-[0_0_36px_rgba(255,42,42,0.55)]"
          >
            Partner With Us
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
          <button
            onClick={() => setOpen((s) => !s)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-zinc-900"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FAF6EC] border-t border-zinc-900/10">
          <div className="max-w-7xl mx-auto px-5 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="text-sm uppercase tracking-[0.2em] text-zinc-700 hover:text-zinc-900 py-2 border-b border-zinc-900/5"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#FF2A2A] text-white text-xs uppercase tracking-[0.2em] font-medium"
            >
              Partner With Us <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

// ============ Hero ============
function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-44 sm:pt-40 md:pt-32 pb-10 md:pb-16 overflow-x-hidden overflow-y-visible grain"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${IMG.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6EC]/96 via-[#FAF6EC]/92 to-[#FAF6EC]" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FF2A2A] opacity-[0.07] blur-[140px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-6 md:gap-10 lg:gap-12 items-start">
        {/* LEFT */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-3 md:mb-4 animate-fade-up" style={{ animationDelay: "0.05s" }}>
            <span className="h-px w-10 bg-[#FF2A2A]" />
            <span className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-zinc-600">
              Empowering Young Creators · NYC
            </span>
          </div>

          <h1
            className="font-display text-[2.2rem] sm:text-4xl md:text-5xl lg:text-6xl leading-[0.98] font-semibold tracking-tight animate-fade-up"
            style={{ animationDelay: "0.15s" }}
          >
            Media Skills.{" "}
            <span className="gradient-cyan-text">Ai Literacy.</span>{" "}
            <span className="gradient-red-text">Business Mindset.</span>
          </h1>

          <p
            className="mt-4 md:mt-5 text-base md:text-lg text-zinc-700 max-w-2xl leading-relaxed animate-fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            The B.L.A.S.T. Academy teaches and mentors young NYC creators ages 16–24
            <span className="text-zinc-900 font-medium"> through the arts</span> —
            hands-on programs in business, leadership, artificial intelligence,
            streaming, and technology.
          </p>

          <div className="mt-5 md:mt-7 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "0.45s" }}>
            <a
              href="/#programs"
              data-testid="hero-explore-programs"
              className="group inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#E60000] text-white px-6 py-3.5 text-sm uppercase tracking-[0.18em] font-medium shadow-[0_0_28px_rgba(255,42,42,0.4)] hover:shadow-[0_0_44px_rgba(255,42,42,0.6)] transition-all"
            >
              Explore Programs
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/#contact"
              data-testid="hero-request-info"
              className="inline-flex items-center gap-2 border border-zinc-900/20 hover:border-zinc-900/50 hover:bg-zinc-900/5 text-zinc-900 px-6 py-3.5 text-sm uppercase tracking-[0.18em] font-medium transition-all"
            >
              Request Info
            </a>
            <a
              href="/teach"
              data-testid="hero-become-instructor"
              className="group inline-flex items-center gap-2 border-2 border-[#FF2A2A] hover:bg-[#FF2A2A] text-[#FF2A2A] hover:text-white px-6 py-3.5 text-sm uppercase tracking-[0.18em] font-medium transition-all"
            >
              Become an Instructor
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Highlights */}
          <div className="mt-6 md:mt-8 grid grid-cols-3 gap-3 md:gap-4 animate-fade-up" style={{ animationDelay: "0.6s" }}>
            {[
              { icon: Wrench, title: "Real-World Skills", body: "Practical, future-ready training." },
              { icon: Sparkles, title: "Creative Confidence", body: "Express, present, produce ideas." },
              { icon: Rocket, title: "Opportunities", body: "Career, business, leadership paths." },
            ].map((h) => (
              <div key={h.title} className="border-l-2 border-[#FF2A2A]/40 pl-3">
                <h.icon className="w-4 h-4 text-[#FF2A2A] mb-1.5" />
                <div className="font-display text-xs md:text-sm font-medium text-zinc-900 tracking-tight leading-tight">
                  {h.title}
                </div>
                <p className="text-[11px] text-zinc-600 mt-0.5 leading-snug">{h.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: stacked program preview cards (no floating logo) */}
        <div className="lg:col-span-5 relative flex flex-col gap-3 md:gap-4 mt-2 lg:mt-0">
          {/* Card 1 — Media */}
          <Link
            to="/programs/blast"
            data-testid="hero-card-media"
            className="block bg-[#ECFEFF] border border-[#00E5FF]/40 p-5 rounded-sm group hover:border-[#00E5FF] hover:shadow-[0_0_24px_rgba(0,229,255,0.18)] hover:-translate-y-0.5 transition-all duration-300"
          >
            <div className="flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-24 h-24 md:w-28 md:h-28 bg-transparent shrink-0 overflow-hidden">
                <img src={IMG.logoBlastWorkshop} alt="The B.L.A.S.T. Academy Workshop" className="w-full h-full object-contain" style={{ mixBlendMode: "multiply" }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display font-semibold text-base md:text-lg text-zinc-900 group-hover:text-[#0891B2] transition-colors leading-tight">The B.L.A.S.T. Academy Workshop</div>
                  <span className="font-mono-display text-[10px] tracking-[0.2em] text-[#00E5FF]/70 shrink-0">01</span>
                </div>
                <div className="text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-[#0891B2] font-medium mt-1">
                  Media · Podcasting · Streaming
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 group-hover:text-[#00E5FF] inline-flex items-center gap-1 transition-colors">
                  Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </div>
          </Link>

          {/* Card 2 — Ai Advantage */}
          <Link
            to="/programs/ai-advantage"
            data-testid="hero-card-ai"
            className="block bg-[#FAF5FE] border border-[#9D4CDD]/40 p-5 rounded-sm group hover:border-[#9D4CDD] hover:shadow-[0_0_24px_rgba(157,76,221,0.2)] hover:-translate-y-0.5 transition-all duration-300"
          >
            <div className="flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-24 h-24 md:w-28 md:h-28 bg-transparent shrink-0 overflow-hidden">
                <img src={IMG.logoAi} alt="The Ai Advantage" className="w-full h-full object-contain" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display font-semibold text-base md:text-lg text-zinc-900 group-hover:gradient-ai-text transition-colors">The Ai Advantage</div>
                  <span className="font-mono-display text-[10px] tracking-[0.2em] text-[#9D4CDD]/70 shrink-0">02</span>
                </div>
                <div className="text-[10px] md:text-[11px] uppercase tracking-[0.22em] gradient-ai-text font-semibold mt-1">
                  Ai for Business &amp; Creativity
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 group-hover:text-[#9D4CDD] inline-flex items-center gap-1 transition-colors">
                  Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </div>
          </Link>

          {/* Card 3 — Young Hustlers */}
          <Link
            to="/programs/young-hustlers"
            data-testid="hero-card-hustlers"
            className="block bg-[#FEFCE8] border border-[#FFD700]/50 p-5 rounded-sm group hover:border-[#CA8A04] hover:shadow-[0_0_24px_rgba(255,215,0,0.25)] hover:-translate-y-0.5 transition-all duration-300"
          >
            <div className="flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 bg-transparent shrink-0 overflow-hidden">
                <img src={IMG.logoHustlers} alt="Young Hustlers Academy" className="w-full h-full object-contain" style={{ mixBlendMode: "multiply" }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display font-semibold text-base md:text-lg text-zinc-900 group-hover:text-[#CA8A04] transition-colors">Young Hustlers Academy</div>
                  <span className="font-mono-display text-[10px] tracking-[0.2em] text-[#CA8A04]/70 shrink-0">03</span>
                </div>
                <div className="text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-[#CA8A04] font-medium mt-1">
                  Entrepreneurship &amp; Career Readiness
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 group-hover:text-[#CA8A04] inline-flex items-center gap-1 transition-colors">
                  Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </div>
          </Link>

          {/* All 5 Boroughs tag */}
          <div className="self-stretch mt-1 inline-flex items-center flex-wrap gap-x-2 gap-y-1 px-3.5 py-2 bg-[#FF2A2A] text-white text-[10px] uppercase tracking-[0.2em] font-medium">
            <MapPin className="w-3 h-3 shrink-0" />
            <span>Manhattan</span><span className="opacity-50">·</span>
            <span>Brooklyn</span><span className="opacity-50">·</span>
            <span>Queens</span><span className="opacity-50">·</span>
            <span>Bronx</span><span className="opacity-50">·</span>
            <span>Staten Island</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Marquee ============
function Marquee() {
  const items = [
    "Podcasting", "Livestreaming", "Ai Literacy", "Prompt Engineering",
    "Entrepreneurship", "Public Speaking", "Video Production", "Brand Building",
    "Financial Literacy", "Leadership", "Storytelling", "Creative Tech",
  ];
  return (
    <div className="border-y border-zinc-900/10 py-5 overflow-hidden bg-[#FAF6EC] w-full max-w-full">
      <div className="flex marquee-track whitespace-nowrap gap-12 w-max">
        {[...items, ...items].map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="text-zinc-500 text-xs md:text-sm uppercase tracking-[0.32em] flex items-center gap-12"
          >
            {t}
            <span className="w-1.5 h-1.5 bg-[#FF2A2A] rotate-45" />
          </span>
        ))}
      </div>
    </div>
  );
}

// ============ About / Pillars ============
const PILLARS = [
  { letter: "B", title: "Business", body: "Money mindset, financial literacy, entrepreneurship, and real-world business skills.", icon: TrendingUp, accent: "#FFD700" },
  { letter: "L", title: "Leadership", body: "Confidence, communication, teamwork, accountability, and public speaking.", icon: Users, accent: "#FF2A2A" },
  { letter: "A", title: "Ai", body: "Use artificial intelligence creatively, ethically, and strategically.", icon: Cpu, accent: "#9D4CDD" },
  { letter: "S", title: "Streaming", body: "Podcasting, video, live streaming, and digital media production.", icon: Radio, accent: "#00E5FF" },
  { letter: "T", title: "Technology", body: "Modern tools and platforms to create, build, solve, and compete.", icon: Sparkles, accent: "#7DF9FF" },
];

function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-14 md:py-32 grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#FF2A2A]" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">01 — About</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1] text-balance">
              What is <span className="gradient-red-text">B.L.A.S.T.</span> Academy?
            </h2>
            <p className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed">
              A youth-centered creative technology and business education platform that
              bridges the gap between traditional education and the skills young people
              need <span className="text-zinc-900">right now</span>. Through hands-on learning,
              real-world projects, and mentorship, participants learn how to create,
              communicate, build, and present ideas using modern tools.
            </p>

            <div className="mt-8 p-5 border border-[#FF2A2A]/40 bg-[#FF2A2A]/[0.04] relative">
              <div className="absolute top-0 left-0 w-2 h-full bg-[#FF2A2A]" />
              <p className="pl-3 text-sm md:text-base text-zinc-900 font-medium leading-relaxed">
                B.L.A.S.T. stands for{" "}
                <span className="gradient-red-text font-semibold">
                  Business, Leadership, Ai, Streaming & Technology.
                </span>
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PILLARS.map((p, i) => (
                <div
                  key={p.letter}
                  data-testid={`pillar-card-${p.title.toLowerCase()}`}
                  className={`reveal group bg-[#FFFFFF] border border-zinc-900/10 p-6 hover:border-zinc-900/30 transition-all duration-300 ${
                    i === 2 ? "sm:col-span-2" : ""
                  }`}
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="font-mono-display text-5xl font-bold leading-none"
                      style={{ color: p.accent }}
                    >
                      {p.letter}
                    </div>
                    <p.icon className="w-5 h-5 text-zinc-500 group-hover:text-zinc-900 transition-colors" />
                  </div>
                  <div className="font-display text-xl font-semibold text-zinc-900 mb-2">{p.title}</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Program Tracks ============
const TRACKS = [
  {
    key: "media",
    slug: "blast",
    title: "The B.L.A.S.T. Academy Workshop",
    subtitle: "Media · Podcasting · Streaming",
    description:
      "The original B.L.A.S.T. Academy. Hands-on training in podcasting, audio production, video production, livestreaming, interviewing, editing, media storytelling, and digital content creation.",
    skills: ["Podcasting", "Audio Production", "Video Production", "Livestreaming", "Interviewing", "Editing", "Storytelling", "Final Media Project"],
    image: IMG.trackMedia,
    logoChip: IMG.logoBlastWorkshop,
    icon: Mic2,
    accent: "#00E5FF",
    accentClass: "text-[#00E5FF]",
    bg: "from-[#00E5FF]/15 to-transparent",
    border: "border-[#00E5FF]/30",
    glow: "shadow-[0_0_60px_rgba(0,229,255,0.18)]",
    badge: "Track 01",
  },
  {
    key: "ai",
    slug: "ai-advantage",
    title: "The Ai Advantage",
    subtitle: "Ai for Business & Creativity",
    description:
      "Learn how to use Ai as a creative and business tool. Participants generate ideas, do research, create marketing content, build simple brands, develop digital products, and pitch business concepts with accessible Ai tools.",
    skills: ["Ai Tool Literacy", "Prompt Writing", "Research", "Business Idea Development", "Branding Basics", "Marketing Content", "Digital Products", "Final Pitch"],
    image: IMG.trackAi,
    logoChip: IMG.logoAi,
    icon: Brain,
    accent: "#9D4CDD",
    accentClass: "gradient-ai-text",
    bg: "from-[#9D4CDD]/15 via-[#E8387B]/10 to-[#FF6B00]/10",
    border: "border-[#9D4CDD]/30",
    glow: "shadow-[0_0_60px_rgba(157,76,221,0.2)]",
    badge: "Track 02",
    isAi: true,
  },
  {
    key: "hustlers",
    slug: "young-hustlers",
    title: "Young Hustlers Academy",
    subtitle: "Entrepreneurship & Real-World Business",
    description:
      "Business ownership, financial literacy, communication, leadership, group economics, career pathways, trades, investing concepts, and practical entrepreneurship.",
    skills: ["Business Basics", "Money Mindset", "Public Speaking", "Sales & Communication", "Career Readiness", "Team Building", "Leadership", "Entrepreneurial Thinking"],
    image: IMG.trackHustlers,
    logoChip: IMG.logoHustlers,
    icon: Briefcase,
    accent: "#FFD700",
    accentClass: "text-[#FFD700]",
    bg: "from-[#FFD700]/15 to-transparent",
    border: "border-[#FFD700]/30",
    glow: "shadow-[0_0_60px_rgba(255,215,0,0.18)]",
    badge: "Track 03",
  },
];

function ProgramTracks() {
  return (
    <section id="programs" data-testid="programs-section" className="relative py-14 md:py-32 bg-[#FFFFFF]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#FF2A2A]" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">02 — Programs</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1]">
            One Academy. <br />
            <span className="text-zinc-500">Multiple Pathways.</span>
          </h2>
          <p className="mt-6 text-zinc-700 text-base md:text-lg max-w-2xl leading-relaxed">
            Each program can operate as a standalone workshop or combine into a larger youth development academy model.
          </p>
        </div>

        <div className="mt-10 md:mt-16 space-y-6">
          {TRACKS.map((t, idx) => (
            <article
              key={t.key}
              data-testid={`program-card-${t.key}`}
              className={`reveal relative grid md:grid-cols-12 gap-0 bg-[#FFFFFF] border ${t.border} overflow-hidden group hover:${t.glow} transition-all duration-500`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              {/* gradient bg */}
              <div className={`absolute inset-0 bg-gradient-to-br ${t.bg} opacity-50`} />

              {/* image side */}
              <div className={`relative md:col-span-5 h-64 md:h-auto md:min-h-[440px] overflow-hidden ${idx % 2 === 1 ? "md:order-2" : ""}`}>
                <img
                  src={t.image}
                  alt={t.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-${idx % 2 === 1 ? "l" : "r"} from-[#FFFFFF]/30 via-transparent to-[#FFFFFF]`} />
              </div>

              {/* content side */}
              <div className={`relative md:col-span-7 p-8 md:p-12 ${idx % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="inline-flex items-center justify-center w-24 h-24 md:w-28 md:h-28 shrink-0 bg-transparent">
                    <img
                      src={t.logoChip}
                      alt={`${t.title} logo`}
                      className="w-full h-full object-contain"
                      style={{ mixBlendMode: "multiply" }}
                    />
                  </span>
                  <div className={`text-[10px] uppercase tracking-[0.28em] font-medium ${t.accentClass}`}>
                    {t.subtitle}
                  </div>
                </div>

                <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-zinc-900">
                  {t.title}
                </h3>
                <p className="mt-5 text-zinc-700 text-sm md:text-base leading-relaxed max-w-xl">
                  {t.description}
                </p>

                <div className="mt-7 grid grid-cols-2 gap-y-2 gap-x-4 max-w-lg">
                  {t.skills.map((s) => (
                    <div key={s} className="flex items-center gap-2 text-[13px] text-zinc-700">
                      <span className="w-1.5 h-1.5 rotate-45" style={{ background: t.accent }} />
                      {s}
                    </div>
                  ))}
                </div>

                <Link
                  to={`/programs/${t.slug}`}
                  data-testid={`program-card-cta-${t.key}`}
                  className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-medium text-zinc-900 hover:text-zinc-600 transition-colors group/cta"
                  style={{ color: t.accent }}
                >
                  Learn More About This Program
                  <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ Why It Matters ============
function WhyMatters() {
  const cards = [
    { icon: Mic2, title: "Confidence", body: "Students practice public speaking, presenting ideas, and expressing themselves clearly." },
    { icon: Sparkles, title: "Creativity", body: "Students create real projects, not just worksheets." },
    { icon: Cpu, title: "Digital Literacy", body: "Modern tools used in business, media, Ai, and technology." },
    { icon: Rocket, title: "Opportunity", body: "Skills they can apply immediately to school, work, business, and creative projects." },
  ];
  return (
    <section data-testid="why-matters-section" className="relative py-14 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 reveal">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#FF2A2A]" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">03 — Why It Matters</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1] text-balance">
              Where young creators <br />
              <span className="gradient-red-text">build skills that last.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-zinc-700 text-base md:text-lg leading-relaxed reveal">
            Through hands-on arts mentorship in media, podcasting, streaming, and Ai —
            paired with practical business and leadership training in Young Hustlers —
            B.L.A.S.T. gives students the tools, confidence, and real-world experience
            to turn creative talent into opportunity.
          </p>
        </div>

        <div className="mt-8 md:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {cards.map((c, i) => (
            <div
              key={c.title}
              data-testid={`impact-card-${c.title.toLowerCase().replace(/\s/g, "-")}`}
              className="reveal group relative bg-[#FFFFFF] border border-zinc-900/10 p-4 md:p-6 hover:border-[#FF2A2A]/50 transition-all duration-300"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF2A2A] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <c.icon className="w-5 h-5 md:w-7 md:h-7 text-[#FF2A2A] mb-3 md:mb-5" />
              <div className="font-mono-display text-[10px] tracking-[0.28em] text-zinc-500 mb-1 md:mb-2">
                0{i + 1}
              </div>
              <h3 className="font-display text-base md:text-xl font-semibold text-zinc-900 mb-1.5 md:mb-2 leading-tight">{c.title}</h3>
              <p className="text-xs md:text-sm text-zinc-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ Flexible Formats ============
function Formats() {
  const formats = [
    { label: "6-Week Workshop", icon: Calendar },
    { label: "8-Week Academy Cycle", icon: GraduationCap },
    { label: "After-School Program", icon: Building2 },
    { label: "Summer Intensive", icon: Sun },
    { label: "Final Showcase", icon: Trophy },
    { label: "Field Trip Experience", icon: MapPin },
  ];
  return (
    <section data-testid="formats-section" className="relative py-14 md:py-32 bg-[#FFFFFF]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#FF2A2A]" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">04 — Formats</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1]">
            Flexible Program <span className="gradient-cyan-text">Formats</span>
          </h2>
          <p className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed">
            Each track can operate as a standalone program or combine into a larger academy model
            based on your organization's needs.
          </p>
        </div>

        <div className="mt-8 md:mt-14 grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {formats.map((f, i) => (
            <div
              key={f.label}
              data-testid={`format-${f.label.toLowerCase().replace(/\s/g, "-")}`}
              className="reveal group flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 bg-[#FFFFFF] border border-zinc-900/10 px-3 md:px-5 py-3.5 md:py-5 hover:border-[#00E5FF]/40 hover:bg-[#ECFEFF] transition-all"
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <span className="inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 bg-[#00E5FF]/10 border border-[#00E5FF]/20 group-hover:bg-[#00E5FF]/20 transition-colors shrink-0">
                <f.icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#00E5FF]" />
              </span>
              <span className="font-display text-xs md:text-sm font-medium text-zinc-900 leading-tight">{f.label}</span>
            </div>
          ))}
        </div>

        <p className="mt-10 text-sm text-zinc-400 max-w-2xl leading-relaxed reveal">
          Programs can be customized for schools, nonprofits, community organizations,
          workforce initiatives, and grant-funded youth programs.
        </p>
      </div>
    </section>
  );
}

// ============ Outcomes ============
function Outcomes() {
  const outcomes = [
    "A completed media, Ai, or business project",
    "Improved confidence and communication skills",
    "Practical Ai literacy",
    "A final showcase, pitch, or presentation",
    "Real-world skills they can keep building",
  ];
  return (
    <section id="outcomes" data-testid="outcomes-section" className="relative py-14 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#FF2A2A]" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">05 — Outcomes</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1] text-balance">
              What Students <br />
              <span className="gradient-gold-text">Walk Away</span> With
            </h2>
            <p className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed">
              Real skills, real projects, real confidence. Every cycle ends with tangible
              outcomes students can show, share, and build on.
            </p>

            <div className="mt-10 hidden lg:block">
              <img
                src={IMG.trackHustlers}
                alt="Students at showcase"
                className="w-full aspect-[4/3] object-cover grayscale-[20%] border border-zinc-900/10"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {outcomes.map((o, i) => (
                <li
                  key={o}
                  data-testid={`outcome-item-${i}`}
                  className="reveal flex items-start gap-3 group border-b border-zinc-900/5 pb-3"
                  style={{ transitionDelay: `${i * 35}ms` }}
                >
                  <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 bg-[#FFD700]/10 border border-[#FFD700]/30 flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#FFD700]" strokeWidth={3} />
                  </span>
                  <span className="text-sm md:text-base text-zinc-800 leading-relaxed">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Community Impact + Partners ============
function Community() {
  const partners = [
    "Schools & Community-Based Orgs",
    "Youth Programs",
    "Grant-Funded Initiatives",
    "Reentry & Justice-Involved Youth",
  ];
  return (
    <section id="partners" data-testid="partners-section" className="relative py-14 md:py-32 bg-[#FFFFFF]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#FF2A2A]" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">06 — Community</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1]">
            Built for <span className="gradient-red-text">Community Impact</span>
          </h2>
          <p className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed">
            For organizations that want to offer young people meaningful enrichment,
            workforce development, creative arts, digital literacy, leadership, and
            entrepreneurship training.
          </p>
        </div>

        {/* Trusted by — NeON Arts (partner) */}
        <div className="mt-10 sm:mt-8 md:mt-14 flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-x-8 gap-y-5 reveal">
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-[#FFD700]" />
            <span className="text-xs uppercase tracking-[0.28em] text-zinc-600">2x Grant Recipient</span>
          </div>
          <div className="h-6 w-px bg-zinc-900/10 hidden md:block" />
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-[#00E5FF]" />
            <span className="text-xs uppercase tracking-[0.28em] text-zinc-600">Carnegie Hall Showcase</span>
          </div>
          <div className="h-6 w-px bg-zinc-900/10 hidden md:block" />
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-transparent">
              <img
                src={IMG.partnerNeon}
                alt="NeON Arts Partner"
                className="h-16 w-auto object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
            <span className="text-xs uppercase tracking-[0.28em] text-zinc-600">NeON Arts Partner</span>
          </div>
          <div className="h-6 w-px bg-zinc-900/10 hidden md:block" />
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-transparent">
              <img
                src={IMG.partnerRYC}
                alt="Renaissance Youth Center"
                className="h-16 w-auto object-contain"
                style={{ mixBlendMode: "lighten" }}
              />
            </div>
            <span className="text-xs uppercase tracking-[0.28em] text-zinc-600">Renaissance Youth Center</span>
          </div>
          <div className="h-6 w-px bg-zinc-900/10 hidden md:block" />
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-transparent">
              <img
                src={IMG.partnerF3}
                alt="Fresh Fast Foods, LLC"
                className="h-16 w-auto object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
            <span className="text-xs uppercase tracking-[0.28em] text-zinc-600">Fresh Fast Foods, LLC</span>
          </div>
        </div>

        <div className="mt-8 md:mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {partners.map((p, i) => (
            <div
              key={p}
              data-testid={`partner-card-${i}`}
              className="reveal group bg-[#FFFFFF] border border-zinc-900/10 p-5 hover:border-[#FF2A2A]/40 transition-all"
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <Heart className="w-4 h-4 text-[#FF2A2A] mb-3 opacity-70 group-hover:opacity-100 transition-opacity" />
              <div className="font-display text-sm font-medium text-zinc-900 leading-snug">{p}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ Founder ============
function Founder() {
  const badges = ["Teaching Artist", "Media Producer", "Entrepreneur", "Mentor", "Business Owner", "Youth Development"];
  return (
    <section data-testid="founder-section" className="relative py-14 md:py-32 grain overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#FF2A2A] opacity-[0.08] blur-[140px] rounded-full -translate-y-1/2" />
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="grid grid-cols-2 gap-3 w-full mx-auto lg:mx-0">
              <div className="relative group overflow-hidden border border-zinc-900/10">
                <img
                  src={IMG.founder}
                  alt="Philip 'Power' Armstrong"
                  className="w-full aspect-[3/4] object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FAF6EC] via-transparent to-[#FAF6EC]/20 pointer-events-none" />
              </div>
              <div className="relative group overflow-hidden border border-zinc-900/10">
                <img
                  src={IMG.founder2}
                  alt="Philip 'Power' Armstrong on set"
                  className="w-full aspect-[3/4] object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FAF6EC] via-transparent to-[#FAF6EC]/20 pointer-events-none" />
                <div className="absolute -bottom-4 -right-4 bg-[#FF2A2A] px-4 py-3 shadow-[0_0_28px_rgba(255,42,42,0.4)]">
                  <div className="font-display font-semibold text-white text-sm leading-tight">Philip "Power"</div>
                  <div className="font-display font-semibold text-white text-sm leading-tight">Armstrong</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 mt-1">Founder · NYC</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#FF2A2A]" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">07 — Founder</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1] text-balance">
              Led by <span className="gradient-red-text">Philip "Power"</span> Armstrong
            </h2>
            <p className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed">
              Philip is an entrepreneur, media producer, teaching artist, mentor, and
              business owner with a background in multimedia production, podcasting,
              music, entrepreneurship, restaurant ownership, and youth development.
              Through B.L.A.S.T. Academy, he brings real-world experience into the
              classroom and helps young people build confidence through hands-on education.
            </p>

            <div className="mt-7 p-5 bg-[#FFFFFF] border border-zinc-900/10">
              <p className="text-sm text-zinc-400 leading-relaxed">
                Past B.L.A.S.T. Academy programming included media, podcasting, live
                streaming, and creative technology instruction. The new B.L.A.S.T. Academy
                expands that foundation into <span className="text-zinc-900">business,
                leadership, Ai, streaming, and technology.</span>
              </p>
            </div>

            <div className="mt-7 flex flex-wrap gap-2">
              {badges.map((b) => (
                <span
                  key={b}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-medium text-zinc-700 border border-zinc-900/15 hover:border-[#FF2A2A] hover:text-zinc-900 transition-colors"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Final CTA ============
function FinalCTA() {
  return (
    <section data-testid="final-cta-section" className="relative py-12 md:py-32 overflow-hidden w-full">
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${IMG.philipTeaching})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6EC] via-[#FAF6EC]/90 to-[#FAF6EC]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#FF2A2A] opacity-[0.12] blur-[140px] rounded-full" />
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <div className="reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF2A2A]/40 bg-[#FF2A2A]/5 mb-8">
            <span className="w-1.5 h-1.5 bg-[#FF2A2A] rounded-full animate-pulse-soft" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] font-medium">
              Accepting Partners · 2026 Cycle
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1] text-balance">
            Bring <span className="gradient-red-text">B.L.A.S.T. Academy</span> to your school,
            organization, or community.
          </h2>
          <p className="mt-7 text-base md:text-xl text-zinc-700 max-w-3xl mx-auto leading-relaxed">
            Let's give young creators the tools to build, communicate, lead, and compete
            in the modern world.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href="/#contact"
              data-testid="final-cta-request-info"
              className="group inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#E60000] text-white px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium shadow-[0_0_36px_rgba(255,42,42,0.45)] hover:shadow-[0_0_56px_rgba(255,42,42,0.65)] transition-all"
            >
              Request Program Info
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/#contact"
              data-testid="final-cta-schedule"
              className="inline-flex items-center gap-2 border border-zinc-900/15 hover:border-zinc-900/50 hover:bg-white/5 text-zinc-900 px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium transition-all"
            >
              Schedule a Conversation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Contact Form ============
const PROGRAM_OPTIONS = [
  "B.L.A.S.T. Academy",
  "The Ai Advantage",
  "Young Hustlers Academy",
  "Full B.L.A.S.T. Academy Model",
  "Custom Program",
  "Partnership / Funding Conversation",
];

function ContactForm() {
  const [form, setForm] = useState({
    fullName: "",
    organization: "",
    email: "",
    phone: "",
    programInterest: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 2) e.fullName = "Please enter your full name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.programInterest) e.programInterest = "Select a program interest.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSuccess(true);
      toast.success("Inquiry received. We'll be in touch shortly.");
      setForm({ fullName: "", organization: "", email: "", phone: "", programInterest: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-14 md:py-32 bg-[#FFFFFF]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#FF2A2A]" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-400">08 — Contact</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1] text-balance">
              Let's <span className="gradient-red-text">talk</span>.
            </h2>
            <p className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed">
              Tell us about your school, organization, or community. We'll follow up with
              program info, formats, and partnership pathways.
            </p>

            <div className="mt-10 space-y-4">
              <a href="mailto:theblastacademy@gmail.com" data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#FF2A2A]/10 border border-[#FF2A2A]/30 group-hover:bg-[#FF2A2A]/20 transition-colors">
                  <Mail className="w-4 h-4 text-[#FF2A2A]" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Email</div>
                  <div className="font-display text-zinc-900 text-sm md:text-base">theblastacademy@gmail.com</div>
                </div>
              </a>
              <a href="tel:+19173702800" data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#00E5FF]/10 border border-[#00E5FF]/30 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <Phone className="w-4 h-4 text-[#00E5FF]" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Phone</div>
                  <div className="font-display text-zinc-900 text-sm md:text-base">(917) 370-2800</div>
                </div>
              </a>
              <a href="https://instagram.com/its_POWER_on_IG" target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 group">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#FFD700]/10 border border-[#FFD700]/30 group-hover:bg-[#FFD700]/20 transition-colors">
                  <Instagram className="w-4 h-4 text-[#FFD700]" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Instagram</div>
                  <div className="font-display text-zinc-900 text-sm md:text-base">@its_POWER_on_IG</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#9D4CDD]/10 border border-[#9D4CDD]/30">
                  <MapPin className="w-4 h-4 text-[#9D4CDD]" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Based In</div>
                  <div className="font-display text-zinc-900 text-sm md:text-base">New York City</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            {success ? (
              <div
                data-testid="contact-success"
                className="bg-[#FFFFFF] border border-[#FFD700]/40 p-10 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFD700]/15 border border-[#FFD700]/30 mb-5">
                  <Check className="w-7 h-7 text-[#FFD700]" strokeWidth={3} />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-zinc-900 mb-3">
                  Thank you.
                </h3>
                <p className="text-zinc-700 leading-relaxed max-w-md mx-auto">
                  Your inquiry has been received. We'll follow up with more information
                  about B.L.A.S.T. Academy.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="contact-send-another"
                  className="mt-7 text-sm uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-900 transition-colors"
                >
                  Send another inquiry →
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-[#FFFFFF] border border-zinc-900/10 p-7 md:p-10 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      data-testid="contact-fullname"
                      value={form.fullName}
                      onChange={(e) => update("fullName", e.target.value)}
                      placeholder="Your full name"
                      className="bg-zinc-50 border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF2A2A] focus-visible:ring-[#FF2A2A]/30 h-12 rounded-sm"
                    />
                    {errors.fullName && <p className="text-xs text-[#FF6262] mt-1.5">{errors.fullName}</p>}
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block">
                      Organization
                    </Label>
                    <Input
                      data-testid="contact-organization"
                      value={form.organization}
                      onChange={(e) => update("organization", e.target.value)}
                      placeholder="School, nonprofit, agency..."
                      className="bg-zinc-50 border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF2A2A] focus-visible:ring-[#FF2A2A]/30 h-12 rounded-sm"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block">
                      Email *
                    </Label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@org.com"
                      className="bg-zinc-50 border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF2A2A] focus-visible:ring-[#FF2A2A]/30 h-12 rounded-sm"
                    />
                    {errors.email && <p className="text-xs text-[#FF6262] mt-1.5">{errors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block">
                      Phone
                    </Label>
                    <Input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="(555) 555-5555"
                      className="bg-zinc-50 border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF2A2A] focus-visible:ring-[#FF2A2A]/30 h-12 rounded-sm"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block">
                    Program Interest *
                  </Label>
                  <Select
                    value={form.programInterest}
                    onValueChange={(v) => update("programInterest", v)}
                  >
                    <SelectTrigger
                      data-testid="contact-program-select"
                      className="bg-zinc-50 border-zinc-300 text-zinc-900 h-12 rounded-sm focus:ring-[#FF2A2A]/30 focus:border-[#FF2A2A]"
                    >
                      <SelectValue placeholder="Select a program..." className="text-zinc-600" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-zinc-900/15 text-zinc-900">
                      {PROGRAM_OPTIONS.map((p) => (
                        <SelectItem
                          key={p}
                          value={p}
                          data-testid={`program-option-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="focus:bg-[#FF2A2A]/15 focus:text-zinc-900"
                        >
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.programInterest && <p className="text-xs text-[#FF6262] mt-1.5">{errors.programInterest}</p>}
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block">
                    Message
                  </Label>
                  <Textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about your goals and how we can help."
                    rows={5}
                    className="bg-zinc-50 border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF2A2A] focus-visible:ring-[#FF2A2A]/30 rounded-sm resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="w-full h-13 py-4 bg-[#FF2A2A] hover:bg-[#E60000] text-white text-sm uppercase tracking-[0.2em] font-medium rounded-sm shadow-[0_0_28px_rgba(255,42,42,0.4)] hover:shadow-[0_0_44px_rgba(255,42,42,0.6)] transition-all disabled:opacity-60"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Send Message <ChevronRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Footer ============
export function Footer() {
  // Update footer link hrefs to be absolute
  return (
    <footer data-testid="site-footer" className="border-t-2 border-[#FF2A2A]/20 bg-[#F2EDDF] py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex w-11 h-11 overflow-hidden bg-transparent shrink-0">
                <img
                  src={IMG.logoMain}
                  alt="B.L.A.S.T. Academy"
                  className="absolute"
                  style={{ width: "200%", height: "auto", maxWidth: "none", left: "-50%", top: "-4%" }}
                />
              </span>
              <div>
                <div className="font-display font-bold text-lg">B.L.A.S.T. ACADEMY</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Since 2024 · Queens, NYC</div>
              </div>
            </div>
            <p className="mt-5 text-sm text-zinc-500 max-w-sm leading-relaxed">
              Business, Leadership, Ai, Streaming & Technology. Mentoring young NYC
              creators through the arts.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-4">Explore</div>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-sm text-zinc-700 hover:text-[#FF2A2A] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-4">Get in Touch</div>
            <ul className="space-y-2.5 text-sm text-zinc-700">
              <li><a href="mailto:theblastacademy@gmail.com" className="hover:text-zinc-900 transition-colors">theblastacademy@gmail.com</a></li>
              <li><a href="tel:+19173702800" className="hover:text-zinc-900 transition-colors">(917) 370-2800</a></li>
              <li><a href="https://instagram.com/its_POWER_on_IG" target="_blank" rel="noreferrer" className="hover:text-zinc-900 transition-colors">@its_POWER_on_IG</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-zinc-900/5 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-600">
            © {new Date().getFullYear()} B.L.A.S.T. Academy · All rights reserved.
          </div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-600">
            Built with intention. Designed for impact.
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============ Page ============
export default function Landing() {
  useReveal();

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main data-testid="landing-page" className="bg-[#FAF6EC] text-zinc-900 min-h-screen">
      <Nav />
      <Hero />
      <Marquee />
      <ProgramTracks />
      <WhyMatters />
      <Formats />
      <Outcomes />
      <Community />
      <Founder />
      <FinalCTA />
      <ContactForm />
      <Footer />
    </main>
  );
}

